import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@vega-api/custom/api/hooks/useAuth";
import { $txId, setTxId } from "@vega-api/custom/stores/txId.store";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { bffSessionCheck } from "@vega-api/generated/authorization-bff/authorization-bff";
import { getGetTasksQueryKey } from "@vega-api/generated/lp-tasks/lp-tasks";
import { FrontendPortal } from "@vega-api/generated/types/frontendPortal";
import { LocalStorageService } from "@vega-core/service/LocalStorage.service";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { type PollOutcome, pollWithRetry } from "./pollWithRetry";

export const TX_ID_CAPTURE_MAX_ATTEMPTS = 10;
export const TX_ID_CAPTURE_RETRY_DELAY_MS = 100;

export const useTxIdCapture = (
  portal: FrontendPortal = FrontendPortal.LP_PORTAL,
) => {
  const { logout } = useAuth();
  const { featureFlag } = useFeatureFlag();
  const flagOn = Boolean(featureFlag[FeatureFlag.LP_AUTH_LOGIN_TASK]);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!flagOn) return;

    if (queryClient.getQueryData(getGetTasksQueryKey()) !== undefined) {
      return;
    }

    const existingTxId = LocalStorageService.getTxId();

    if (existingTxId !== null) {
      if ($txId.get() === null) {
        $txId.set(existingTxId);
      }

      return;
    }

    if (LocalStorageService.getLpTasksCompleted()) return;

    return pollWithRetry({
      maxAttempts: TX_ID_CAPTURE_MAX_ATTEMPTS,
      delayMs: TX_ID_CAPTURE_RETRY_DELAY_MS,
      run: async ({ signal }): Promise<PollOutcome> => {
        const response = await bffSessionCheck(undefined, signal);
        const txId = response.data?.txId;

        if (isTruthy(txId)) {
          setTxId(txId);

          return "done";
        }

        return "retry";
      },
      onExhausted: () => {
        void logout({
          isGlobal: true,
          savePath: false,
          source: portal,
        });
      },
    });
  }, [flagOn, logout, portal, queryClient]);
};
